import { forwardRef } from 'react';
import styled from 'styled-components';
import { kassimBlue, kassimGreen } from '../../style/theme';
import { useGetTextParameter } from '../../helpers/hooks';
import {
    ComboBox,
    TextBox,
    NumberBox,
    Datepicker,
    DateTimepicker,
    TextArea,
    TextLabel,
    InputTable,
} from './Controls';

const FieldCheckbox = styled.input.attrs(() => ({ type: 'checkbox' }))`
    width: 13px;
    margin: 3px 3px 3px 4px;
    box-sizing: border-box;
`;
// ${({ $forControlType }) =>
//     $forControlType === 'table' && `margin-top: calc(0em + 3px)`}

const FieldLabel = styled.label`
    width: 200px;
    /* display: inline-block; */
    flex-basis: 200px;
    flex-shrink: 0;
    ${({ $help }) => $help && `cursor: help;`}
    ${({ $header }) =>
        $header &&
        `
    font-weight: bold;
    color: ${kassimBlue};
  `}
    ${(props) => props.error && `color: red;`} 
    ${({ $indent }) =>
        $indent &&
        `
    margin-left: 20px;
    `}
`;
const FieldContainer = styled.div`
    font-size: 12px;
    margin-bottom: 4px;
    border-bottom: 1px dotted ${kassimGreen};
    display: flex;
    align-items: flex-start;
    ${({ $header }) =>
        $header &&
        `
    margin-top: 24px;
    margin-bottom: 8px;
    border-bottom: 1px solid ${kassimBlue};

    &:first-child {
        margin-top: 0;
    }
  `}

    ${({ $table }) =>
        $table &&
        `
    margin-top: 8px;
    margin-bottom: 8px;

    & label, & ${FieldCheckbox} {
        margin-top: 20px;
    }    
  `}
`;
// ${({ $forControlType }) => $forControlType === 'table' && `margin-top: 1em`}
// @todo
// @media (max-width: 1200px) {
//     flex-basis: 150px;
//     width: 150px;
// }

const FieldPostfix = styled.div`
    /* width: 80px; */
    padding-left: 4px;
    /* display: inline; */
    box-sizing: border-box;
    font-size: 90%;
    padding-top: 0.2em;
`;

/**
 *
 * @param {*} props
 * @param {string} props.control - textbox | datepicker | combobox
 * @param {string[][]} items - combobox items, string per option per language
 * @param {string[]} text - label text per language
 * @param {*} defaultValue
 * @param {*} value
 * @param {func} onChange
 * @returns
 */
const ParameterControl = forwardRef(
    (
        {
            id,
            className,
            hideCheckbox = false,
            control,
            customControl,
            controlwidth,
            items,
            text,
            col1,
            col2,
            placeholder,
            step,
            bounds,
            value,
            enableable,
            readonly,
            onChange,
            languageId = 0,
            unit,
            autoFocus,
            error,
            helperText,
            url: helpUrl,
            variant = 'default',
        },
        ref
    ) => {
        const getTextParameter = useGetTextParameter();
        const inputValue =
            value && typeof value === 'object' && !Array.isArray(value)
                ? value.value
                : value;
        const handleChange = (newValue) => {
            if (typeof value === 'object' && !Array.isArray(value)) {
                onChange({ ...value, value: newValue });
            } else {
                onChange(newValue);
            }
        };
        const toggleCheckbox = () => {
            if (typeof value === 'object') {
                onChange({ ...value, enabled: !value.enabled });
            }
        };
        const getInputControl = () => {
            const commonInputProps = {
                id,
                label: getTextParameter(text),
                value: inputValue,
                controlwidth,
                placeholder,
                autoFocus,
                readonly,
                error,
                helperText,
                variant,
            };
            switch (control) {
                case 'combobox':
                    return (
                        <ComboBox
                            {...commonInputProps}
                            onChange={handleChange}
                            items={items.map((item, i) => ({
                                // value: values ? values[i] : item[languageId],
                                value: i,
                                label: item[languageId],
                            }))}
                        />
                    );
                case 'textbox':
                    return (
                        <TextBox
                            {...commonInputProps}
                            onChange={handleChange}
                            ref={ref}
                        />
                    );
                case 'numberbox':
                    return (
                        <NumberBox
                            {...commonInputProps}
                            step={step}
                            bounds={bounds}
                            onChange={handleChange}
                            ref={ref}
                        />
                    );
                case 'datepicker':
                    return (
                        <Datepicker
                            {...commonInputProps}
                            onChange={handleChange}
                        />
                    );
                case 'datetimepicker':
                    return (
                        <DateTimepicker
                            {...commonInputProps}
                            onChange={handleChange}
                        />
                    );
                case 'textarea':
                    return (
                        <TextArea
                            {...commonInputProps}
                            onChange={handleChange}
                        />
                    );
                case 'header':
                    return null;
                // return <Header>{commonInputProps.label}</Header>;
                case 'textlabel':
                    return <TextLabel {...commonInputProps} />;
                case 'custom':
                    return customControl;
                case 'table':
                    return (
                        <InputTable
                            {...commonInputProps}
                            headerLabels={[
                                getTextParameter(col1),
                                getTextParameter(col2),
                            ]}
                            onChange={handleChange}
                        />
                    );
                default:
                    return null;
            }
        };

        const label = getTextParameter(text);

        const onClickLabel = () => {
            window.open(helpUrl, 'help_window');
        };

        return (
            <FieldContainer
                className={className}
                $header={control === 'header'}
                $table={control === 'table'}
            >
                {enableable && !hideCheckbox && (
                    <FieldCheckbox
                        id={`${id}-enabled`}
                        name={`${id}-enabled`}
                        checked={value.enabled}
                        onChange={toggleCheckbox}
                        $forControlType={control}
                    />
                )}
                {label && (
                    <FieldLabel
                        htmlFor={id}
                        $header={control === 'header'}
                        $indent={!enableable && !hideCheckbox}
                        error={error}
                        onClick={helpUrl && onClickLabel}
                        $help={!!helpUrl}
                        $forControlType={control}
                    >
                        {label}
                        {control ? '' : unit && ` (${getTextParameter(unit)})`}
                    </FieldLabel>
                )}
                {getInputControl()}
                {control && unit && (
                    <FieldPostfix
                        dangerouslySetInnerHTML={{
                            __html: getTextParameter(unit),
                        }}
                    />
                )}
            </FieldContainer>
        );
    }
);
export default ParameterControl;
